import React, { useEffect, useState } from 'react';
import { AppBar, Box, IconButton, Toolbar, Typography, Badge, Stack } from '@mui/material';
import { ShoppingBag, ArrowBack, AccountCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../store/cart';
import { useLang } from '../i18n/LanguageProvider';
import { useAuth } from '../context/AuthContext';
import { palette } from '../theme';

export const Header: React.FC<{ onOpenCart: () => void }> = ({ onOpenCart }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();
  const { lang, setLang } = useLang();
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const showBack = location.pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <AppBar
      position="fixed"
      sx={{
        py: scrolled ? 0.5 : 1.5,
        px: { xs: 1, sm: 2 },
        zIndex: 100,
        background: scrolled ? `${palette.bg}ee` : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(160%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(160%)' : 'none',
        borderBottom: scrolled ? `1px solid ${palette.border}` : '1px solid transparent',
        transition: 'all .4s cubic-bezier(.22,1,.36,1)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
        {showBack ? (
          <IconButton edge="start" onClick={() => navigate(-1)} aria-label="back" sx={{ color: palette.primary }}>
            <ArrowBack />
          </IconButton>
        ) : null}

        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            cursor: 'pointer',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src="/saheeh_logo_w.svg"
            alt="Saheeh"
            sx={{
              width: scrolled ? 36 : 44,
              height: scrolled ? 36 : 44,
              filter: `drop-shadow(0 2px 10px ${palette.primary}55)`,
              transition: 'all .35s cubic-bezier(.22,1,.36,1)',
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#fff',
                fontFamily: '"Inter Tight", Inter, sans-serif',
                fontWeight: 800,
                fontSize: { xs: '18px', sm: '20px' },
                letterSpacing: '2px',
                lineHeight: 1.1,
              }}
            >
              SAHEEH
            </Typography>
            <Typography sx={{ color: `${palette.primary}cc`, fontSize: '10px', letterSpacing: '2px', fontWeight: 500 }}>
              صحيح · ORDER
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{
          background: 'rgba(255,255,255,0.06)',
          border: `1px solid ${palette.border}`,
          borderRadius: 999,
          p: 0.4,
        }}>
          {(['en', 'ar'] as const).map((l) => (
            <Box
              key={l}
              onClick={() => setLang(l)}
              role="button"
              sx={{
                px: 1.4,
                py: 0.4,
                borderRadius: 999,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                cursor: 'pointer',
                textTransform: 'uppercase',
                color: lang === l ? palette.bg : palette.textMuted,
                background: lang === l ? palette.primary : 'transparent',
                transition: 'all .25s',
              }}
            >
              {l}
            </Box>
          ))}
        </Stack>

        <IconButton
          onClick={() => navigate(isAuthenticated ? '/account' : '/auth')}
          aria-label={isAuthenticated ? 'open account' : 'sign in'}
          sx={{ color: palette.primary }}
        >
          <AccountCircle />
        </IconButton>

        <IconButton onClick={onOpenCart} aria-label="open cart" sx={{ color: palette.primary, ml: 0.5 }}>
          <Badge
            badgeContent={count}
            sx={{
              '& .MuiBadge-badge': {
                background: palette.primary,
                color: palette.bg,
                fontWeight: 700,
                boxShadow: `0 0 0 2px ${palette.bg}`,
              },
            }}
          >
            <ShoppingBag />
          </Badge>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};
